import { type ChangeEvent, type FocusEvent, useEffect, useState } from 'react';

import { useAtomValue } from 'jotai';

import { filtersListAtom } from '@core/Atoms/Plp/FiltersListAtom';
import { aggregationsAtom } from '@core/Atoms/Search/AggregationsAtom';
import type { Filter, FilterKey } from '@core/Entities/Search/SearchFilters/Filter.entity';
import { getFilterFromArray } from '@core/Utils/SearchFilters/GetFilterFromArray';

import { trackFilterEvent } from 'Components/Plp/Filters/Filters.tracking';
import { getClosestIndexInRange } from 'Utils/SearchFilters/GetClosestIndexInRange';

import { useUpdateFilters } from './UseUpdateFilters';

const useRangeFilters = (
  filterName: string,
  range: number[],
  defaultValues: [string | undefined, string | undefined] = [undefined, undefined],
  applyButton?: boolean,
) => {
  const filtersList = useAtomValue(filtersListAtom);
  const { updateFilters } = useUpdateFilters();
  const gteId = `${filterName}__gte` as FilterKey;
  const lteId = `${filterName}__lte` as FilterKey;
  const { totalCount } = useAtomValue(aggregationsAtom);

  const [gteValue, setGteValue] = useState(
    (getFilterFromArray(gteId, filtersList)?.value as string | undefined) || defaultValues[0],
  );
  const [lteValue, setLteValue] = useState(
    (getFilterFromArray(lteId, filtersList)?.value as string | undefined) || defaultValues[1],
  );
  const [localGteValue, setLocalGteValue] = useState(
    (getFilterFromArray(gteId, filtersList)?.value as string | undefined) || defaultValues[0],
  );
  const [localLteValue, setLocalLteValue] = useState(
    (getFilterFromArray(lteId, filtersList)?.value as string | undefined) || defaultValues[1],
  );

  const handleChange = (changes: [FilterKey, string | number | boolean | undefined][]) => {
    const filterChanges: Filter[] = changes.map(change => {
      return {
        id: change[0],
        value: change[1]?.toString() || undefined,
      };
    });

    if (!applyButton) {
      trackFilterEvent(
        filterChanges.length === 1 ? filterChanges[0].id : filterName,
        filterChanges.map(change => change.value) as string[],
        filtersList,
      );

      updateFilters(filterChanges);
    }
  };

  // need to update the state values once filters atom changes
  useEffect(() => {
    setGteValue(
      (getFilterFromArray(gteId, filtersList)?.value as string | undefined) || defaultValues[0],
    );
    setLteValue(
      (getFilterFromArray(lteId, filtersList)?.value as string | undefined) || defaultValues[1],
    );
  }, [filtersList, range.join()]);

  // Reset local values when filterName changes
  useEffect(() => {
    setLocalGteValue(
      (getFilterFromArray(gteId, filtersList)?.value as string | undefined) || defaultValues[0],
    );
    setLocalLteValue(
      (getFilterFromArray(lteId, filtersList)?.value as string | undefined) || defaultValues[1],
    );
  }, [filterName]);

  // text/number input functions
  const onGteInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setGteValue(e.target.value);
    setLteValue(lteValue);
  };
  const onLteInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setGteValue(gteValue);
    setLteValue(e.target.value);
  };
  const onGteInputBlur = (e: FocusEvent<HTMLInputElement, Element>) => {
    handleChange([
      [gteId, e.target.value],
      [lteId, lteValue],
    ]);
  };
  const onLteInputBlur = (e: FocusEvent<HTMLInputElement, Element>) => {
    handleChange([
      [gteId, gteValue],
      [lteId, e.target.value],
    ]);
  };

  //reset functions
  const canReset = (additionalFilterIds: FilterKey[] = []) => {
    if (applyButton) {
      return localGteValue !== defaultValues[0] || localLteValue !== defaultValues[1];
    } else {
      return (
        filtersList.filter(fltr => {
          const idsToCheck = [...additionalFilterIds, gteId, lteId];
          return idsToCheck.includes(fltr.id);
        }).length > 0
      );
    }
  };
  const reset = (additionalFilterIds: FilterKey[] = []) => {
    const additionalFiltersCleared = additionalFilterIds.map(fltrId => ({
      id: fltrId,
      value: undefined,
    }));

    if (applyButton) {
      setLocalGteValue(defaultValues[0] || '');
      setLocalLteValue(defaultValues[1] || '');
    } else {
      updateFilters([
        ...additionalFiltersCleared,
        { id: gteId, value: undefined },
        { id: lteId, value: undefined },
      ]);
    }
  };

  // slider functions
  const onSliderChange = (values: [number, number]) => {
    if (applyButton) {
      setLocalGteValue(range[values[0]].toString());
      setLocalLteValue(range[values[1]].toString());
    } else {
      setGteValue(range[values[0]].toString());
      setLteValue(range[values[1]].toString());
    }
  };
  const onSliderAfterChange = (values: [number, number]) => {
    if (!applyButton) {
      handleChange([
        [gteId, range[values[0]]],
        [lteId, range[values[1]]],
      ]);
    }
  };

  const sliderStepCount = range.length;
  const sliderSelectedRangeIndexes: [number, number] = [
    getClosestIndexInRange(Number(applyButton ? localGteValue : gteValue), range),
    getClosestIndexInRange(Number(applyButton ? localLteValue : lteValue), range),
  ];

  return {
    handleChange,
    canReset,
    reset,
    totalCount,
    gteInputProps: {
      onBlur: applyButton ? undefined : onGteInputBlur,
      onChange: applyButton ? undefined : onGteInputChange,
      value: applyButton ? localGteValue : gteValue,
    },
    lteInputProps: {
      onBlur: applyButton ? undefined : onLteInputBlur,
      onChange: applyButton ? undefined : onLteInputChange,
      value: applyButton ? localLteValue : lteValue,
    },
    sliderProps: {
      onChange: onSliderChange,
      onAfterChange: onSliderAfterChange,
      stepCount: sliderStepCount,
      selectedRangeIndexes: sliderSelectedRangeIndexes,
    },
  };
};

export { useRangeFilters };
